import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { NotificationService } from '../../../../../services/notifications.service';
import { IntegrationTelegramService } from '../../../../../services/integration-telegram.service';
import {
  SendCodeRequest,
  SendCodeResponse,
  SignInWithPasswordRequest,
  VerifyCodeRequest,
} from './integration-telegram-user.interface';
import { IError } from '../../../../../interfaces';
import { CHANNEL_TYPE, DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations } from '../../../../../enum/constants';
import { WebSocketService } from '../../../../../services/gateway.service';
import { trimValidator } from '../../../../../helpers';
import { getIntegrationQrCode } from '../../../../../helpers/utils';
import { ChannelService } from '../../../../../services/channel.service';

@Component({
  selector: 'app-integration-telegram-user',
  templateUrl: './integration-telegram-user.component.html',
  styleUrls: ['./integration-telegram-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IntegrationTelegramUserComponent implements OnInit, OnDestroy {
  @ViewChild('qrcode') qrCodeCanvas!: ElementRef;
  private readonly destroy$ = new Subject<null>();
  private channelId!: string;
  private sendCodeResponse!: SendCodeResponse | null;
  private qrCodeRequest!: Subscription;
  protected channelName!: string;
  protected qrCode!: string | null;
  protected formGroup!: FormGroup<{ phoneNumber: FormControl }>;
  protected phoneCode!: FormControl | null;
  protected password?: FormControl | null;
  protected loading: boolean = false;
  protected alreadyAuth: boolean = false;

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    private readonly integrationTelegramService: IntegrationTelegramService,
    private readonly notificationService: NotificationService,
    private readonly webSocketService: WebSocketService,
    private readonly channelService: ChannelService,
  ) {}

  ngOnInit() {
    this.channelId = this.payload.channel.id;
    this.channelName = this.payload.channel.name;

    this.formGroup = new FormGroup({
      phoneNumber: new FormControl('', [Validators.required, trimValidator]),
    });

    this.checkAuthorization();
    this.handleWebSocket();
  }

  ngOnDestroy() {
    if (this.qrCodeRequest) this.qrCodeRequest.unsubscribe();
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private checkAuthorization() {
    this.loading = true;
    this.integrationTelegramService.checkAuthorization(this.channelId).subscribe({
      next: (data) => {
        if (data && data.phone_number) this.formGroup.controls.phoneNumber.setValue(data.phone_number);
        if (data && data.is_auth) this.alreadyAuth = true;
        this.loading = false;
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
          );
        });
        this.loading = false;
      },
    });
  }

  protected onLogOut() {
    this.loading = true;
    this.integrationTelegramService.logOut(this.channelId).subscribe({
      next: () => {
        this.alreadyAuth = false;
        this.loading = false;
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
          );
        });
        this.loading = false;
      },
    });
  }

  protected handleWebSocket() {
    this.webSocketService
      .getQRCodeRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.is_password) {
            if (this.qrCodeCanvas?.nativeElement) this.qrCodeCanvas.nativeElement.innerHTML = '';
            this.password = new FormControl('', [Validators.required, trimValidator]);
          } else if (res?.code) {
            this.notificationService.showError(
              activeTranslations.notifications_texts.error.error,
              ERROR_MESSAGES[res.code] || DEFAULT_ERROR,
            );
          } else {
            this.notificationService.showSuccess(
              activeTranslations.notifications_texts.success.success,
              activeTranslations.notifications_texts.success.successfully_saved,
            );
            this.close.emit('success');
          }
          this.loading = false;
        },
      });
  }

  private initQRCode() {
    if (!this.qrCodeCanvas?.nativeElement || !this.qrCode) return;

    const qrCode = getIntegrationQrCode(this.qrCode, '/assets/svgs/integrations/telegram_qrcode_canvas.png');

    this.qrCodeCanvas.nativeElement.innerHTML = '';
    qrCode.append(this.qrCodeCanvas.nativeElement);
  }

  protected onSendCode() {
    this.channelService.checkConnect({ type: CHANNEL_TYPE.TELEGRAM }).subscribe({
      next: (data: any) => {
        if (data) {
          if (this.formGroup.controls.phoneNumber.invalid) {
            this.formGroup.controls.phoneNumber.markAsTouched();
            return;
          }
          this.loading = true;

          const payload: SendCodeRequest = {
            phone_number: this.formGroup.controls.phoneNumber.getRawValue(),
            channel_id: this.channelId,
          };
          this.integrationTelegramService.sendCode(payload).subscribe({
            next: (res: SendCodeResponse) => {
              this.phoneCode = new FormControl('', [Validators.required, trimValidator]);
              this.sendCodeResponse = res;
              this.loading = false;
            },
            error: (e: any) => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError(
                  activeTranslations.notifications_texts.error.error,
                  ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
                );
              });
              this.loading = false;
            },
          });
        }
      },
    });
  }

  protected onCancelCode() {
    this.phoneCode = null;
    this.sendCodeResponse = null;
  }

  protected onVerifyCode() {
    if (!this.sendCodeResponse || !this.phoneCode || this.phoneCode?.invalid) {
      this.phoneCode?.markAsTouched();
      return;
    }
    this.loading = true;

    const payload: VerifyCodeRequest = {
      phone_code: this.phoneCode.getRawValue(),
      phone_number: this.formGroup.controls.phoneNumber.getRawValue(),
      channel_id: this.channelId,
      phone_code_hash: this.sendCodeResponse.phone_code_hash,
    };
    this.integrationTelegramService.verifyCode(payload).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          activeTranslations.notifications_texts.success.success,
          activeTranslations.notifications_texts.success.successfully_saved,
        );
        this.close.emit('success');
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          if (err.code === 'SESSION_PASSWORD_NEEDED') {
            this.password = new FormControl('', [Validators.required, trimValidator]);
            return;
          }
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
          );
          this.phoneCode?.reset();
        });
        this.loading = false;
      },
    });
  }

  protected onSignInWithPassword() {
    if (!this.password || this.password?.invalid) {
      this.password?.markAsTouched();
      return;
    }
    this.loading = true;

    const payload: SignInWithPasswordRequest = {
      password: this.password.getRawValue(),
      channel_id: this.channelId,
    };
    this.integrationTelegramService.signInWithPassword(payload).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          activeTranslations.notifications_texts.success.success,
          activeTranslations.notifications_texts.success.successfully_saved,
        );
        this.close.emit('success');
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
          );
          this.password?.reset();
        });

        if ((e.error?.errors || []).some((err: any) => err?.code === 'CHANNEL_HAS_ANOTHER_PHONE')) {
          this.initQRCode();
          this.password = undefined;
        }

        this.loading = false;
      },
    });
  }

  protected openQrLogin() {
    if (this.formGroup.controls.phoneNumber.invalid) {
      this.formGroup.controls.phoneNumber.markAsTouched();
      return;
    }

    this.loading = true;

    this.qrCodeRequest = this.integrationTelegramService.signInWithQRCode(this.channelId).subscribe({
      next: (qrLink) => {
        this.loading = false;
        this.qrCode = qrLink;
        requestAnimationFrame(() => requestAnimationFrame(() => this.initQRCode()));
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
          );
        });
        this.loading = false;
      },
    });
  }

  protected openPhoneLogin() {
    this.loading = true;
    if (this.qrCodeRequest) this.qrCodeRequest.unsubscribe();
    this.qrCode = null;
    this.loading = false;
  }

  protected copyChannelId() {
    const data = this.channelId;
    navigator.clipboard
      .writeText(data)
      .then(() => {
        this.notificationService.showSuccess(
          '',
          activeTranslations.notifications_texts.info.copied,
        );
      })
      .catch((_) => {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error
            .there_was_an_error_please_try_againn,
        );
      });
  }
}
